import { portfolioDetailQueryOptions } from "@/api/hooks/portfolio";
import type { LayerUnionWithAll, RenderableLayerUnion } from "@/layers";
import { LayerKindName } from "@/layers/utility/createLayerObject";
import type { Portfolio } from "@/portfolio/types";
import type { PortfolioEnergyPoiLayer } from "@/reducer/client-models/PoiLayer";
import { useQueries } from "@tanstack/react-query";
import { useMemo, useRef } from "react";

function useStableValues<T>(values: T[]) {
  const stable = useRef(values);
  if (stable.current.length !== values.length || stable.current.some((value, index) => value !== values[index])) {
    stable.current = values;
  }
  return stable.current;
}

/**
 * Resolves persisted portfolio references before descriptions cross into the
 * imperative rendering layer. Portfolio data remains transient and is never
 * written back to Redux or the backend.
 */
export function useRenderablePortfolioEnergyLayers(
  layers: LayerUnionWithAll[] | undefined,
): RenderableLayerUnion[] | undefined {
  const portfolioIds = useMemo(
    () => [
      ...new Set(
        (layers ?? [])
          .filter(
            (layer): layer is PortfolioEnergyPoiLayer =>
              layer.kind === LayerKindName.PortfolioEnergyPoiLayerDescription,
          )
          .map((layer) => layer.poiOptions.portfolioId),
      ),
    ],
    [layers],
  );
  const results = useQueries({ queries: portfolioIds.map(portfolioDetailQueryOptions) });
  const portfolios = useStableValues(
    results.map((result) => (result.data as Portfolio | null | undefined) ?? undefined),
  );

  return useMemo(() => {
    if (!layers) {
      return undefined;
    }
    const portfoliosById = new Map(portfolioIds.map((id, index) => [id, portfolios[index]]));
    return layers.map((layer) =>
      layer.kind === LayerKindName.PortfolioEnergyPoiLayerDescription
        ? { ...layer, portfolio: portfoliosById.get(layer.poiOptions.portfolioId) }
        : layer,
    ) as RenderableLayerUnion[];
  }, [layers, portfolioIds, portfolios]);
}
